import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
} from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Observable, of } from "rxjs";
import {
    filter,
    map,
    mergeMap,
    shareReplay,
    switchMap,
    take,
} from "rxjs/operators";

import { REGISTER } from "../../../account/components/register/register.graphql";
import {
    GetOrderByCodeQuery,
    GetOrderByCodeQueryVariables,
    RegisterMutation,
    RegisterMutationVariables,
} from "../../../common/generated-types";
import { SUPPORT_MAILTO } from "../../../common/constants";
import { notNullOrUndefined } from "../../../common/utils/not-null-or-undefined";
import { isConsolidatedFeedOrder } from "../../../common/utils/is-consolidated-feed-order";
import { DataService } from "../../../core/providers/data/data.service";

import {
    ConfirmationOrder,
    ConfirmationOrderLineProductVariant,
    OrderLine,
    shippingSurchargeAmount,
    shippingSurchargeLabel,
    SupplierGroup,
} from "../../checkout.types";
import { GET_ORDER_BY_CODE } from "./checkout-confirmation.graphql";

@Component({
    selector: "vsf-checkout-confirmation",
    templateUrl: "./checkout-confirmation.component.html",
    styleUrls: ["./checkout-confirmation.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckoutConfirmationComponent implements OnInit {
    registrationSent = false;
    readonly supportMailto = SUPPORT_MAILTO;
    order$: Observable<GetOrderByCodeQuery["orderByCode"]>;
    notFound$: Observable<boolean>;
    paymentStatus: string | null = null;
    groupedLines$: Observable<SupplierGroup[]>;

    constructor(
        private dataService: DataService,
        private changeDetector: ChangeDetectorRef,
        private route: ActivatedRoute
    ) {}

    ngOnInit() {
        const orderRequest$ = this.route.paramMap.pipe(
            map((paramMap) => paramMap.get("code")),
            filter(notNullOrUndefined),
            switchMap((code) =>
                this.dataService.query<
                    GetOrderByCodeQuery,
                    GetOrderByCodeQueryVariables
                >(GET_ORDER_BY_CODE, { code })
            ),
            map((data) => data.orderByCode),
            shareReplay(1)
        );
        this.order$ = orderRequest$.pipe(filter(notNullOrUndefined));
        this.notFound$ = orderRequest$.pipe(map((res) => !res));

        this.groupedLines$ = this.order$.pipe(
            map((order) => this.groupLinesBySupplier(order as ConfirmationOrder))
        );

        this.route.queryParamMap.subscribe((params) => {
            this.paymentStatus = params.get("redirect_status");
        });
    }

    masterOrderCode(order: {
        code: string;
        aggregateOrderId?: string | null;
        aggregateOrder?: { code?: string | null } | null;
        customFields?: { isMain?: boolean | null; isConsolidatedFeed?: boolean | null } | null;
    }): string {
        if (order.customFields?.isMain === false && order.aggregateOrder?.code) {
            return order.aggregateOrder.code;
        }
        return order.code;
    }

    isBillingSameAsShipping(order: {
        billingAddress?: {
            fullName?: string | null;
            streetLine1?: string | null;
            postalCode?: string | null;
            city?: string | null;
        } | null;
        shippingAddress?: {
            fullName?: string | null;
            streetLine1?: string | null;
            postalCode?: string | null;
            city?: string | null;
        } | null;
    }): boolean {
        const bill = order.billingAddress;
        const ship = order.shippingAddress;
        if (!bill?.streetLine1 || !ship?.streetLine1) {
            return false;
        }
        const norm = (v: string | null | undefined) =>
            String(v ?? "").trim().toLowerCase();
        return (
            norm(bill.streetLine1) === norm(ship.streetLine1) &&
            norm(bill.postalCode) === norm(ship.postalCode) &&
            norm(bill.city) === norm(ship.city) &&
            norm(bill.fullName) === norm(ship.fullName)
        );
    }

    private groupLinesBySupplier(
        order: ConfirmationOrder | null | undefined
    ): SupplierGroup[] {
        const lines = order?.lines ?? [];
        const groups = lines.reduce((acc: Record<string, SupplierGroup>, line: OrderLine) => {
            const variant = line.productVariant as ConfirmationOrderLineProductVariant;
            const pricing = variant.pricing;
            const supplierId = pricing?.id != null ? String(pricing.id) : "unknown";
            const supplierName = pricing?.id
                ? `Supplier #${pricing.id}`
                : "Unknown Supplier";

            if (!acc[supplierId]) {
                acc[supplierId] = {
                    supplierId,
                    supplierName,
                    channelOrderCode: null,
                    lines: [],
                    totalValue: 0,
                    itemCount: 0,
                };
            }
            acc[supplierId].lines.push(line);
            acc[supplierId].totalValue +=
                line.discountedLinePriceWithTax ?? line.linePriceWithTax;
            acc[supplierId].itemCount += line.quantity;
            return acc;
        }, {});

        const grouped = Object.values(groups);
        if (isConsolidatedFeedOrder(order)) {
            return grouped;
        }
        const sellerOrders = (order?.sellerOrders ?? []).filter(
            (sellerOrder) => sellerOrder.customFields?.isMain !== true
        );
        for (const sellerOrder of sellerOrders) {
            if (!sellerOrder.code) {
                continue;
            }
            const variantIds = new Set(
                (sellerOrder.lines || [])
                    .map((line) =>
                        line.productVariant?.id != null
                            ? String(line.productVariant.id)
                            : null
                    )
                    .filter((id): id is string => !!id)
            );
            const pricingIds = new Set(
                (sellerOrder.lines || [])
                    .map((line) =>
                        line.productVariant?.pricing?.id != null
                            ? String(line.productVariant.pricing.id)
                            : null
                    )
                    .filter((id): id is string => !!id)
            );
            const group = grouped.find(
                (g) =>
                    g.lines.some((line) =>
                        variantIds.has(String(line.productVariant.id))
                    ) || pricingIds.has(g.supplierId)
            );
            if (group && !group.channelOrderCode) {
                group.channelOrderCode = sellerOrder.code;
            }
        }
        return grouped;
    }

    register() {
        this.order$
            .pipe(
                take(1),
                mergeMap((order) => {
                    const customer = order?.customer;
                    if (customer) {
                        return this.dataService.mutate<
                            RegisterMutation,
                            RegisterMutationVariables
                        >(REGISTER, {
                            input: {
                                emailAddress: customer.emailAddress,
                                firstName: customer.firstName,
                                lastName: customer.lastName,
                            },
                        });
                    } else {
                        return of({});
                    }
                })
            )
            .subscribe(() => {
                this.registrationSent = true;
                this.changeDetector.markForCheck();
            });
    }

    getOriginalSubtotal(
        order: NonNullable<GetOrderByCodeQuery["orderByCode"]>
    ): number {
        if (!order.lines?.length) return order.subTotalWithTax ?? 0;
        return order.lines.reduce(
            (sum, line) => sum + line.unitPriceWithTax * line.quantity,
            0
        );
    }

    shippingCost(
        order: NonNullable<GetOrderByCodeQuery["orderByCode"]>
    ): number | null {
        const amount = shippingSurchargeAmount(order);
        return amount > 0 ? amount : null;
    }

    shippingCostLabel(
        order: NonNullable<GetOrderByCodeQuery["orderByCode"]>
    ): string {
        const amount = shippingSurchargeAmount(order);
        return amount > 0 ? shippingSurchargeLabel(order) : "Shipping with protection:";
    }

    getTotalDiscount(
        order: NonNullable<GetOrderByCodeQuery["orderByCode"]>
    ): number {
        if (!order.discounts?.length) return 0;
        return order.discounts.reduce(
            (sum, d) => sum + Number(d.amountWithTax ?? 0),
            0
        );
    }
}
